import { msToMinutes, minutesToMs, calculateItemDurationFromKeys } from ".";
import { ClassNameFunction, ItemData, ItemId, ItemTuple, minutes, SlotKey } from "./types";
import { CalendarItem } from "../components/CalendarItem";
import { useImmer } from "use-immer";
import { startOfDay } from "./dates";
import { useCallback, useMemo } from "react";

const findGroup = (key: number, groups: Record<number, number[]>): number => {
  return Object.values(groups).findIndex((slotKeys) => slotKeys.includes(key));
};

const findGroupMax = (slotKeys: number[]): number => {
  if (slotKeys.length <= 1) return slotKeys[0]
  const size = slotKeys[1] - slotKeys[0];
  return slotKeys[slotKeys.length - 1] + size;
};

// export function alignItemsToTimeSlot(slotSize: minutes, items: ItemData[]): ItemData[] {
//   return items.map((item) => {
//     const start = item.start.getTime();
//     const slotKey = start - (start % minutesToMs(slotSize));
//     if (slotKey === start) return item; // Avoid creating a new object if already aligned
//     return { ...item, start: new Date(slotKey) };
//   });
// }

// export function alignItemsToDaySlot(items: ItemData[]): ItemData[] {
//   // Note: Timezones only matter for days, and mean you can't use the modolu trick here
//   return items.map((item) => {
//     const slotKey = startOfDay(item.start).getTime()
//     if (slotKey === item.start.getTime()) return item; // Avoid creating a new object if already aligned
//     return { ...item, start: new Date(slotKey) };
//   });
// }

// TODO: This is algorithmically silly, but it works for now
const alignToSlot = (slotSize: number, slotKeyGroups: Record<number, number[]>) => (item: ItemData): ItemData => {
  const start = item.start.getTime();
  const end = start + minutesToMs(item.duration);

  // Does item *start* within a group?
  Object.values(slotKeyGroups).map((group) => {
    const groupEnd = group[group.length - 1] + slotSize;
    if(group[0] <= start && start <= groupEnd) {
      const slotKey = start - (start % minutesToMs(slotSize));
      if (slotKey === start) return item; // Avoid creating a new object if already aligned
      return { ...item, start: new Date(slotKey) };
    }
  })

  // Does item *end* within a group? (And, logically, doesn't start in any group)
  Object.values(slotKeyGroups).map((group) => {
    const groupEnd = group[group.length - 1] + slotSize;
    if(group[0] <= end && end <= groupEnd) {
      return { 
        ...item, 
        start: new Date(group[0]),
        duration: msToMinutes(end - group[0])
      };
    }
  })

  // If the item is still even on this calendar, that means it spans a group
  const calendarStart = slotKeyGroups[0][0];
  const calendarEnd = slotKeyGroups[Object.keys(slotKeyGroups).length - 1].slice(-1)[0] + slotSize;
  if(calendarStart <= start && end <= calendarEnd) {
    const startKey = Object.values(slotKeyGroups).flat().find(key => key >= start);

    // Item starts and ends before the first slot key; it's within the span of the calendar,
    // but not in any group
    if(end < startKey!) { return item; }

    // Item starts before some group, but extends into (or to the end of) that group
    return {
      ...item,
      start: new Date(startKey!),
      duration: msToMinutes(end - startKey!)
    }
  }

  // Ensure we return something
  return item;
}

const calculateDurations = (
  startTime: number,
  groupMax: number,
  nextGroupStart: number,
  itemDuration: number
) => {
  const fittingDuration = msToMinutes(groupMax - startTime);
  const gapDuration = msToMinutes(nextGroupStart - groupMax);
  const remainingDuration = itemDuration - fittingDuration - gapDuration;
  return { fittingDuration, remainingDuration };
};

export function splitItemData(
  start: Date,
  duration: minutes,
  slotKeyGroups: Record<number, number[]>
): {start: Date, duration: minutes}[] {
  const startTime = start.getTime();
  const endTime = startTime + minutesToMs(duration);

  const groupValues = Object.values(slotKeyGroups);
  const groupIdx = findGroup(startTime, slotKeyGroups);

  if (groupIdx < 0) return [];

  const slotKeys = groupValues[groupIdx];
  if (slotKeys.length < 2) return [{start, duration}];

  const groupMax = findGroupMax(slotKeys);

  // The item fits entirely within the group
  if (endTime <= groupMax) return [{start, duration}];

  // The item is in the last group (reduce apparent duration)
  if (groupIdx === groupValues.length - 1) {
    return [{ start, duration: msToMinutes(groupMax - startTime) }];
  }

  const nextGroup = groupValues[groupIdx + 1];
  const nextGroupStart = Math.min(...nextGroup);

  const { fittingDuration, remainingDuration } = calculateDurations(
    startTime,
    groupMax,
    nextGroupStart,
    duration
  );

  // Overflow does not cover the gap (e.g., calendar is 9-5)
  if (remainingDuration <= 0) return [{ start, duration: fittingDuration }];

  return [
    { start, duration: fittingDuration },
    ...splitItemData(
      new Date(nextGroupStart),
      remainingDuration,
      slotKeyGroups
    ),
  ];
};

export function useItems(
  itemsData: ItemData[],
  slotKeyGroups: Record<number, number[]>,
  slotSize: number,
  expansionDimension: 'width' | 'height' = 'height',
  className: ClassNameFunction,
  onClick?: (itemId: ItemId) => void,
): Record<SlotKey, ItemTuple[]> {
  const [ hoveringItemId, setHoveringItemId ] = useImmer<ItemId | null>(null);

  const item = useCallback(
    ({duration, itemId}) => ({ children }) => (
      <CalendarItem
        duration={duration}
        slotSize={slotSize}
        expansionDimension={expansionDimension}
        className={className({
          isHovered: hoveringItemId === itemId
        })}
        callbacks={{
          onMouseOver: () => setHoveringItemId(itemId),
          onMouseLeave: () => setHoveringItemId(null),
          onClick: () => {
            if(onClick) { 
              onClick(itemId); 
            } else {
              console.log(`Clicked item ${itemId}`);
            }
          }
        }}
        
      >
        {children}
      </CalendarItem>
    ),
    [slotSize, expansionDimension, className]
  );

  const alignedData = useMemo(
    () => itemsData.map(alignToSlot(slotSize, slotKeyGroups)),
    [itemsData, slotSize]
  );

  return alignedData.reduce((acc, itemData) => {
    splitItemData(
      itemData.start, 
      itemData.duration, 
      slotKeyGroups
    ).map(({start, duration}) => {
      const key = start.getTime();
      if (!acc[key]) { acc[key] = []; }
      acc[key].push({
        key,
        itemId: itemData.itemId,
        Item: item({ duration, itemId: itemData.itemId }),
      });
    });
    return acc;
  }, {});
}

export function displayItems(
  ItemContents: React.FC<{itemId: ItemId}>,
  map: Record<SlotKey, ItemTuple[]>,
  slotKey: SlotKey,
) {
  return <>{(
    map[slotKey] || []
  ).map(({key, itemId, Item}) => 
    <Item key={key}>
      <ItemContents itemId={itemId} />
    </Item>
  )}</>
}

export const addItem = (setData, slotSize) => (start: SlotKey, end: SlotKey) => {
  const duration = calculateItemDurationFromKeys(start, end, slotSize);
  setData((draft) => [{start: new Date(start), duration, itemId: (draft.length + 1)}, ...draft]);
};